/**
 * Recent Chats — sidebar (ChatPage) and dropdown (ChatPanel) renderer.
 *
 * AI Chat V1.1 Item 4 (DR #43, 2026-05-03). Layout per handoff §Step 5.
 * Brand book: no rounded corners, sentence case, IBM Plex, warm grays,
 * Feather icons, no exclamation marks.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Edit2, MessageSquare, Plus, Search, Trash2, X } from 'react-feather';
import { Skeleton } from '../../../components/ui';

import type { SessionMeta } from '../../../stores/chatStore';

interface Props {
  sessions: SessionMeta[];
  activeSessionId: string | null;
  isLoading: boolean;
  onSelect: (sessionId: string) => void;
  onNew: () => void;
  onRename: (sessionId: string, title: string) => void;
  onDelete: (sessionId: string) => void;
  /** Condensed rendering for the ChatPanel dropdown (no date headers, denser rows). */
  condensed?: boolean;
}

function relativeTime(iso: string | null): string {
  if (!iso) {return '';}
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) {return 'just now';}
  if (minutes < 60) {return `${minutes}m ago`;}
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {return `${hours}h ago`;}
  const days = Math.floor(hours / 24);
  if (days < 7) {return `${days}d ago`;}
  return new Date(iso).toLocaleDateString();
}

function groupByDate(sessions: SessionMeta[]) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const byBucket = { today: [] as SessionMeta[], yesterday: [] as SessionMeta[], older: [] as SessionMeta[] };
  for (const s of sessions) {
    const ref = new Date(s.lastMessageAt ?? s.startedAt);
    if (ref >= today) {byBucket.today.push(s);}
    else if (ref >= yesterday) {byBucket.yesterday.push(s);}
    else {byBucket.older.push(s);}
  }
  return byBucket;
}

/**
 * Group sessions by deal name. Sessions without a `dealName` collect under
 * "General" — always rendered last so a user with deal-scoped chats sees
 * those first and the unscoped catch-all sits at the bottom.
 *
 * Within each group, session order is preserved from the input (API returns
 * sessions newest-first by `last_message_at`).
 */
function groupByDeal(sessions: SessionMeta[]) {
  const byDeal = new Map<string, SessionMeta[]>();
  const general: SessionMeta[] = [];
  for (const s of sessions) {
    const dealName = s.dealName?.trim();
    if (dealName) {
      const existing = byDeal.get(dealName) ?? [];
      existing.push(s);
      byDeal.set(dealName, existing);
    } else {
      general.push(s);
    }
  }
  const sortedDeals = [...byDeal.entries()].sort(([a], [b]) => a.localeCompare(b));
  return { sortedDeals, general };
}

type ViewMode = 'recent' | 'by-deal';

interface RowProps {
  session: SessionMeta;
  isActive: boolean;
  onSelect: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
  condensed?: boolean;
}

function SessionRow({ session, isActive, onSelect, onRename, onDelete, condensed }: RowProps) {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [draft, setDraft] = useState(session.title);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (editing) {
      setDraft(session.title);
      // Focus on next tick so the input exists.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [editing, session.title]);

  // Move focus to the confirm button so Enter immediately confirms and
  // Escape cancels — closer to a native dialog without being a modal.
  useEffect(() => {
    if (confirmingDelete) {
      requestAnimationFrame(() => confirmButtonRef.current?.focus());
    }
  }, [confirmingDelete]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== session.title) {
      onRename(trimmed);
    }
    setEditing(false);
  };

  const handleRowClick = () => {
    if (editing) {return;}
    // Clicking outside the confirm/cancel buttons while in confirm mode
    // dismisses the prompt rather than selecting the chat.
    if (confirmingDelete) {
      setConfirmingDelete(false);
      return;
    }
    onSelect();
  };

  const subtitleParts: string[] = [];
  if (session.dealName) {subtitleParts.push(session.dealName);}
  const timeAgo = relativeTime(session.lastMessageAt ?? session.startedAt);
  if (timeAgo) {subtitleParts.push(timeAgo);}

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        if (editing) {return;}
        if (confirmingDelete && e.key === 'Escape') {
          e.preventDefault();
          setConfirmingDelete(false);
          return;
        }
        if (!confirmingDelete && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect();
        }
      }}
      className={[
        'group flex items-start gap-2 border-l-2 px-3 cursor-pointer',
        condensed ? 'py-2' : 'py-2.5',
        confirmingDelete
          ? 'border-l-error bg-error-light'
          : isActive
            ? 'border-l-orange bg-surface-subtle'
            : 'border-l-transparent hover:bg-surface-subtle',
      ].join(' ')}
    >
      <div className="min-w-0 flex-1">
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setEditing(false);
                setDraft(session.title);
              }
            }}
            className="w-full border border-border bg-surface-card px-2 py-1 text-body-sm text-txt-primary focus:border-orange focus:outline-none"
            maxLength={255}
          />
        ) : (
          <p className="truncate text-body-sm font-medium text-txt-primary">
            {session.title || 'New chat'}
          </p>
        )}
        {!editing && (
          confirmingDelete ? (
            <p className="mt-0.5 truncate text-caption text-error">
              Delete this chat?
            </p>
          ) : (
            subtitleParts.length > 0 && (
              <p className="mt-0.5 truncate text-caption text-txt-tertiary">
                {subtitleParts.join(' · ')}
              </p>
            )
          )
        )}
      </div>

      {!editing && !confirmingDelete && (
        <div className="flex flex-shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(true);
            }}
            aria-label="Rename chat"
            title="Rename"
            className="p-1 text-txt-tertiary hover:text-txt-primary"
          >
            <Edit2 size={12} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmingDelete(true);
            }}
            aria-label="Delete chat"
            title="Delete"
            className="p-1 text-txt-tertiary hover:text-error"
          >
            <Trash2 size={12} />
          </button>
        </div>
      )}

      {!editing && confirmingDelete && (
        <div className="flex flex-shrink-0 items-center gap-1">
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmingDelete(false);
              onDelete();
            }}
            aria-label="Confirm delete"
            title="Delete"
            className="p-1 text-error hover:text-error focus:outline-none"
          >
            <Check size={14} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmingDelete(false);
            }}
            aria-label="Cancel delete"
            title="Cancel"
            className="p-1 text-txt-tertiary hover:text-txt-primary"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="flex flex-col gap-2 p-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <Skeleton variant="text" lines={2} />
        </div>
      ))}
    </div>
  );
}

export function RecentChats({
  sessions,
  activeSessionId,
  isLoading,
  onSelect,
  onNew,
  onRename,
  onDelete,
  condensed = false,
}: Props) {
  // Sidebar-only filter. Hidden in the condensed (panel-dropdown) variant —
  // that surface is too narrow and the list is already short.
  const [query, setQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('recent');
  const trimmedQuery = query.trim().toLowerCase();
  const filteredSessions = useMemo(() => {
    if (!trimmedQuery) {return sessions;}
    return sessions.filter((s) => {
      const title = (s.title || '').toLowerCase();
      const dealName = (s.dealName || '').toLowerCase();
      return title.includes(trimmedQuery) || dealName.includes(trimmedQuery);
    });
  }, [sessions, trimmedQuery]);
  const groupedByDate = useMemo(() => groupByDate(filteredSessions), [filteredSessions]);
  const groupedByDeal = useMemo(() => groupByDeal(filteredSessions), [filteredSessions]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col gap-2 border-b border-border p-3">
        <button
          type="button"
          onClick={onNew}
          className="flex w-full items-center justify-center gap-2 border border-border bg-surface-card px-3 py-2 text-body-sm font-medium text-txt-primary transition-colors hover:border-orange hover:text-orange"
        >
          <Plus size={14} />
          <span>New chat</span>
        </button>
        {!condensed && sessions.length > 0 && (
          <div className="relative">
            <Search
              size={12}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-txt-tertiary"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search chats…"
              className="w-full border border-border bg-surface-card py-1.5 pl-7 pr-7 text-caption text-txt-primary placeholder:text-txt-tertiary focus:border-orange focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-txt-tertiary hover:text-txt-primary"
              >
                <X size={12} />
              </button>
            )}
          </div>
        )}
      </div>

      {!condensed && sessions.length > 0 && (
        <div className="flex border-b border-border" role="tablist">
          {(
            [
              { id: 'recent', label: 'Recent' },
              { id: 'by-deal', label: 'By deal' },
            ] as const
          ).map((tab) => {
            const active = viewMode === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setViewMode(tab.id)}
                className={[
                  'flex-1 px-2 py-1.5 text-caption font-medium transition-colors',
                  active
                    ? 'text-orange border-b-2 border-orange -mb-px'
                    : 'text-txt-tertiary border-b-2 border-transparent hover:text-txt-primary',
                ].join(' ')}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="custom-scrollbar flex-1 overflow-y-auto">
        {isLoading ? (
          <SkeletonRows />
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <MessageSquare size={20} className="text-txt-tertiary" />
            <p className="text-body-sm text-txt-tertiary">No chats yet.</p>
            <p className="text-caption text-txt-tertiary">
              Start a conversation to see it here.
            </p>
          </div>
        ) : trimmedQuery && filteredSessions.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
            <Search size={18} className="text-txt-tertiary" />
            <p className="text-body-sm text-txt-tertiary">No matches</p>
            <p className="text-caption text-txt-tertiary">
              Try a different keyword or clear the search.
            </p>
          </div>
        ) : condensed ? (
          <div className="flex flex-col">
            {filteredSessions.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                isActive={s.id === activeSessionId}
                onSelect={() => onSelect(s.id)}
                onRename={(title) => onRename(s.id, title)}
                onDelete={() => onDelete(s.id)}
                condensed
              />
            ))}
          </div>
        ) : viewMode === 'by-deal' ? (
          <div className="flex flex-col">
            {groupedByDeal.sortedDeals.map(([dealName, rows]) => (
              <div key={dealName}>
                <p className="px-3 pt-3 pb-1 text-overline text-txt-tertiary truncate">
                  {dealName}
                </p>
                {rows.map((s) => (
                  <SessionRow
                    key={s.id}
                    session={s}
                    isActive={s.id === activeSessionId}
                    onSelect={() => onSelect(s.id)}
                    onRename={(title) => onRename(s.id, title)}
                    onDelete={() => onDelete(s.id)}
                  />
                ))}
              </div>
            ))}
            {groupedByDeal.general.length > 0 && (
              <div>
                <p className="px-3 pt-3 pb-1 text-overline text-txt-tertiary">General</p>
                {groupedByDeal.general.map((s) => (
                  <SessionRow
                    key={s.id}
                    session={s}
                    isActive={s.id === activeSessionId}
                    onSelect={() => onSelect(s.id)}
                    onRename={(title) => onRename(s.id, title)}
                    onDelete={() => onDelete(s.id)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col">
            {(['today', 'yesterday', 'older'] as const).map((bucket) => {
              const rows = groupedByDate[bucket];
              if (rows.length === 0) {return null;}
              const label =
                bucket === 'today' ? 'Today' : bucket === 'yesterday' ? 'Yesterday' : 'Older';
              return (
                <div key={bucket}>
                  <p className="px-3 pt-3 pb-1 text-overline text-txt-tertiary">{label}</p>
                  {rows.map((s) => (
                    <SessionRow
                      key={s.id}
                      session={s}
                      isActive={s.id === activeSessionId}
                      onSelect={() => onSelect(s.id)}
                      onRename={(title) => onRename(s.id, title)}
                      onDelete={() => onDelete(s.id)}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
